import { FC, ReactNode, RefObject, useCallback, useEffect, useRef } from 'react';

import { Box, SxProps, Theme } from '@mui/material';

import useVerticalScrollbar from '@/shared/lib/hooks/useVerticalScrollbar';

import { StyledScrollableContainer } from './ScrollableContentContainer.styles';

interface ScrollableContentContainerProps {
  id?: string;
  children: ReactNode;
  maxHeight?: string;
  hasNoScrollBar?: boolean;
  hideTrack?: boolean;
  smoothScroll?: boolean;
  smoothScrollSpeed?: number;
  /** Applied to the outer, non-scrolling wrapper — use for layout participation (flex, margin). */
  sx?: SxProps<Theme>;
  /** Applied to the actual scrolling element — use for presentation overrides (padding, height). */
  innerSx?: SxProps<Theme>;
  scrollRef?: RefObject<HTMLDivElement | null>;
}

const ScrollableContentContainer: FC<ScrollableContentContainerProps> = ({
  id,
  children,
  maxHeight,
  hasNoScrollBar = false,
  hideTrack = false,
  smoothScroll = false,
  smoothScrollSpeed = 0.5,
  sx,
  innerSx,
  scrollRef,
}) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;

      if (scrollRef) {
        scrollRef.current = node;
      }
    },
    [scrollRef],
  );

  useVerticalScrollbar(outerRef, {
    enabled: !hasNoScrollBar,
    alwaysSolid: true,
    hideTrack,
    getScroller: () => containerRef.current,
    getTrackParent: (_scroller, outer) => outer,
    getTrackBounds: (scroller) => ({ top: 0, height: scroller.clientHeight }),
  });

  useEffect(() => {
    if (!smoothScroll || !containerRef.current) return;

    const container = containerRef.current;
    const originalScrollBehavior = container.style.scrollBehavior;
    container.style.scrollBehavior = 'auto';

    const canElementAbsorbScroll = (element: HTMLElement, deltaY: number): boolean => {
      const { overflowY } = window.getComputedStyle(element);
      if (overflowY !== 'auto' && overflowY !== 'scroll') return false;
      if (element.scrollHeight <= element.clientHeight) return false;
      return deltaY < 0
        ? element.scrollTop > 0
        : element.scrollTop < element.scrollHeight - element.clientHeight;
    };

    const handleWheel = (e: WheelEvent) => {
      let node = e.target instanceof HTMLElement ? e.target : null;
      while (node && node !== container) {
        if (canElementAbsorbScroll(node, e.deltaY)) return;
        node = node.parentElement;
      }

      e.preventDefault();
      container.scrollTop += e.deltaY * smoothScrollSpeed;
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.style.scrollBehavior = originalScrollBehavior;
    };
  }, [smoothScroll, smoothScrollSpeed]);

  return (
    <Box
      ref={outerRef}
      sx={[{ position: 'relative', width: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <StyledScrollableContainer
        ref={setContainerRef}
        id={id}
        maxHeight={maxHeight}
        hasNoScrollBar={hasNoScrollBar}
        smoothScroll={smoothScroll}
        sx={innerSx}
      >
        {children}
      </StyledScrollableContainer>
    </Box>
  );
};

export default ScrollableContentContainer;
